import { datePl, type MeDto, todayInWarsaw, VEHICLE_FUELS, VEHICLE_OWNERSHIP, type VehicleDto } from '@bqcrm/shared';
import { Alert, Badge, Button, Group, Modal, SimpleGrid, Stack, Table, Tabs, Text, Textarea, TextInput } from '@mantine/core';
import { IconCarOff, IconPencil, IconRestore, IconAlertTriangle } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { type ReactNode, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { api } from '../../api';
import { ask } from '../../confirm';
import { BlocksUseBadge, CompanyBadgeById, ObligationStateBadge, VehicleAvailabilityBadge, VehicleStatusBadge } from '../../components/Badges';
import { PageHeader } from '../../components/PageHeader';
import { QueryState } from '../../components/QueryState';
import { SectionCard } from '../../components/ui';
import { notifyError, notifyOk } from '../../notify';
import { FleetShell, kmPl, useFleetInvalidate } from './common';
import { VehicleFormModal } from './VehiclesPage';
import { AssignmentTab } from './vehicle/AssignmentTab';
import { CostsTab, DocumentsTab, EventsTab, VehicleReportsTab } from './vehicle/RecordsTabs';
import { MaintenanceTab } from './vehicle/MaintenanceTab';
import { OdometerTab, TyresTab, UnavailabilityTab } from './vehicle/OpsTabs';

const TABS = ['podsumowanie', 'przypisanie', 'terminy', 'zgloszenia', 'opony', 'przebieg', 'dostepnosc', 'koszty', 'dokumenty', 'historia'] as const;
type Tab = (typeof TABS)[number];

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{children || '—'}</Text>
    </div>
  );
}

function PlateModal({ v, onClose }: { v: VehicleDto; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [plate, setPlate] = useState('');
  const [validFrom, setValidFrom] = useState(todayInWarsaw());
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => api<VehicleDto>(`/fleet/vehicles/${v.id}/plate`, { method: 'POST', body: { plate: plate.trim(), validFrom, note } }),
    onSuccess: () => {
      notifyOk('Zmieniono numer rejestracyjny');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Zmiana numeru rejestracyjnego">
      <Stack gap="sm">
        <Text size="sm">Ten sam pojazd dostaje nowy numer; poprzedni zostaje w historii numerów (dopasowanie faktur i transakcji uwzględnia datę).</Text>
        <TextInput label="Nowy numer" required value={plate} onChange={(e) => setPlate(e.currentTarget.value.toUpperCase())} data-autofocus />
        <TextInput label="Obowiązuje od" type="date" required value={validFrom} onChange={(e) => setValidFrom(e.currentTarget.value)} />
        <TextInput label="Uwagi" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={plate.trim().length < 2 || !validFrom} loading={save.isPending} onClick={() => save.mutate()}>
            Zmień numer
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RetireModal({ v, onClose }: { v: VehicleDto; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [date, setDate] = useState(todayInWarsaw());
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => api<VehicleDto>(`/fleet/vehicles/${v.id}/retire`, { method: 'POST', body: { date, reason: reason.trim() } }),
    onSuccess: () => {
      notifyOk('Wycofano pojazd');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Wycofanie pojazdu">
      <Stack gap="sm">
        <Text size="sm">Pojazd znika z bieżących list i przypomnień. Dokumenty, koszty i historia zostają — wycofany pojazd jest w raportach za okresy, w których był używany.</Text>
        <TextInput label="Data wycofania" type="date" required value={date} onChange={(e) => setDate(e.currentTarget.value)} />
        <Textarea label="Powód" required placeholder="np. sprzedaż, koniec leasingu, szkoda całkowita" autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} data-autofocus />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button color="red" disabled={reason.trim().length < 3 || !date} loading={save.isPending} onClick={() => save.mutate()}>
            Wycofaj pojazd
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function SummaryTab({ me, v }: { me: MeDto; v: VehicleDto }) {
  const invalidate = useFleetInvalidate();
  const [edit, setEdit] = useState(false);
  const [plate, setPlate] = useState(false);
  const [retire, setRetire] = useState(false);
  const restore = useMutation({
    mutationFn: () => api<VehicleDto>(`/fleet/vehicles/${v.id}/restore`, { method: 'POST' }),
    onSuccess: () => {
      notifyOk('Przywrócono pojazd');
      invalidate();
    },
    onError: notifyError,
  });
  const manage = v.can.manage;
  return (
    <Stack gap="md">
      {v.status === 'wycofany' && (
        <Alert color="gray" icon={<IconCarOff size={18} />} title={`Pojazd wycofany ${datePl(v.retiredAt)}`}>
          {v.retireReason}
        </Alert>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <SectionCard
          title="Dane pojazdu"
          actions={
            manage && (
              <Group gap={4}>
                <Button size="xs" variant="light" leftSection={<IconPencil size={14} />} onClick={() => setEdit(true)}>
                  Edytuj
                </Button>
                <Button size="xs" variant="default" onClick={() => setPlate(true)}>
                  Zmień numer
                </Button>
              </Group>
            )
          }
        >
          <SimpleGrid cols={2} spacing="sm">
            <Field label="Numer rejestracyjny">{v.plate}</Field>
            <Field label="Firma">
              <CompanyBadgeById id={v.companyId} full />
            </Field>
            <Field label="Marka i model">{[v.make, v.model].filter(Boolean).join(' ')}</Field>
            <Field label="VIN">{v.vin}</Field>
            <Field label="Rodzaj / przeznaczenie">{v.kind}</Field>
            <Field label="Paliwo / napęd">{v.fuel ? VEHICLE_FUELS[v.fuel] : ''}</Field>
            <Field label="Forma użytkowania">{VEHICLE_OWNERSHIP[v.ownership]}</Field>
            {v.ownership !== 'wlasny' && <Field label="Finansujący, umowa">{[v.financier, v.contractNumber].filter(Boolean).join(', ')}</Field>}
            <Field label="Użytkowanie">{v.useFrom || v.useTo ? `${v.useFrom ? `od ${datePl(v.useFrom)}` : ''} ${v.useTo ? `do ${datePl(v.useTo)}` : ''}`.trim() : ''}</Field>
          </SimpleGrid>
          {v.note && (
            <Text size="sm" mt="sm" style={{ whiteSpace: 'pre-line' }}>
              {v.note}
            </Text>
          )}
        </SectionCard>
        <Stack gap="md">
          <SectionCard title="Stan">
            <Stack gap="xs">
              <Group gap="xs">
                <VehicleStatusBadge status={v.status} />
                <VehicleAvailabilityBadge unavailable={v.unavailable} />
                {v.urgentReports > 0 && <BlocksUseBadge />}
              </Group>
              <Field label="Przypisanie">
                {v.assignment ? `${v.assignment.holder === 'ekipa' ? `Ekipa ${v.assignment.team?.name ?? ''}` : (v.assignment.user?.name ?? '')} · odpowiedzialny: ${v.assignment.responsible.name}` : 'bez przypisania'}
              </Field>
              {v.assignment?.leaderChanged && (
                <Alert color="orange" variant="light" py={6} icon={<IconAlertTriangle size={16} />}>
                  Ekipa ma nowego lidera — potwierdź odpowiedzialnego w zakładce „Przypisanie i historia”.
                </Alert>
              )}
              <Field label="Ostatni przebieg">
                {v.odometer ? (
                  <>
                    {kmPl(v.odometer.km)} · odczyt {datePl(v.odometer.readAt)}{' '}
                    {v.odometer.stale && (
                      <Badge size="xs" color="yellow" variant="light">
                        nieaktualny
                      </Badge>
                    )}
                  </>
                ) : (
                  'brak odczytu'
                )}
              </Field>
              <Field label="Najbliższa czynność">
                {v.next ? (
                  <Group gap={6} component="span">
                    {v.next.typeName} <ObligationStateBadge state={v.next.state} reason={v.next.reason} size="xs" />
                  </Group>
                ) : (
                  ''
                )}
              </Field>
              <Field label="Otwarte zgłoszenia">{v.openReports ? `${v.openReports}${v.urgentReports ? ` (pilne: ${v.urgentReports})` : ''}` : 'brak'}</Field>
            </Stack>
          </SectionCard>
          <SectionCard title="Historia numerów" padding={0}>
            <Table>
              <Table.Tbody>
                {v.plates.map((p) => (
                  <Table.Tr key={`${p.plate}-${p.validFrom}`}>
                    <Table.Td fw={600}>{p.plate}</Table.Td>
                    <Table.Td>
                      od {datePl(p.validFrom)}
                      {p.validTo ? ` do ${datePl(p.validTo)}` : ' (bieżący)'}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </SectionCard>
        </Stack>
      </SimpleGrid>
      {manage && (
        <Group justify="flex-end">
          {v.status === 'aktywny' ? (
            <Button color="red" variant="subtle" leftSection={<IconCarOff size={16} />} onClick={() => setRetire(true)}>
              Wycofaj pojazd
            </Button>
          ) : (
            <Button
              variant="light"
              leftSection={<IconRestore size={16} />}
              loading={restore.isPending}
              onClick={async () => (await ask('Pojazd wróci na listy i do przypomnień.', { title: 'Przywrócić pojazd?', confirm: 'Przywróć' })) && restore.mutate()}
            >
              Przywróć pojazd
            </Button>
          )}
        </Group>
      )}
      {edit && <VehicleFormModal me={me} vehicle={v} onClose={() => setEdit(false)} />}
      {plate && <PlateModal v={v} onClose={() => setPlate(false)} />}
      {retire && <RetireModal v={v} onClose={() => setRetire(false)} />}
    </Stack>
  );
}

/** Karta pojazdu — zakładki (?zakladka=…); linki z powiadomień otwierają właściwą zakładkę. */
export function VehiclePage({ me }: { me: MeDto }) {
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const q = useQuery({ queryKey: ['fleet', 'vehicle', id], queryFn: () => api<VehicleDto>(`/fleet/vehicles/${id}`), enabled: !!id });
  const v = q.data;
  const asked = params.get('zakladka') as Tab | null;
  const allowed = (t: Tab) => (t === 'koszty' ? !!v?.can.costs : true);
  const tab: Tab = asked && TABS.includes(asked) && allowed(asked) ? asked : 'podsumowanie';
  const highlight = params.get('czynnosc');
  const setTab = (t: string | null) => t && setParams(t === 'podsumowanie' ? {} : { zakladka: t }, { replace: true });

  return (
    <FleetShell me={me}>
      <QueryState isLoading={q.isLoading} error={q.error}>
        {v && (
          <>
            <PageHeader
              crumbs={[
                { label: 'Flota', to: '/flota' },
                { label: 'Pojazdy', to: '/flota/pojazdy' },
                { label: v.plate },
              ]}
              title={v.plate}
              badge={
                <Group gap={6}>
                  <VehicleStatusBadge status={v.status} />
                  <VehicleAvailabilityBadge unavailable={v.unavailable} />
                </Group>
              }
              description={[[v.make, v.model].filter(Boolean).join(' '), v.kind, v.companyName].filter(Boolean).join(' · ')}
            />
            <Tabs value={tab} onChange={setTab} keepMounted={false}>
              <Tabs.List mb="md">
                <Tabs.Tab value="podsumowanie">Podsumowanie</Tabs.Tab>
                <Tabs.Tab value="przypisanie" rightSection={v.assignment?.leaderChanged ? <IconAlertTriangle size={14} color="var(--mantine-color-orange-6)" /> : undefined}>
                  Przypisanie i historia
                </Tabs.Tab>
                <Tabs.Tab value="terminy">Terminy i serwisy</Tabs.Tab>
                <Tabs.Tab
                  value="zgloszenia"
                  rightSection={
                    v.openReports ? (
                      <Badge size="xs" color={v.urgentReports ? 'red' : 'blue'} variant="filled">
                        {v.openReports}
                      </Badge>
                    ) : undefined
                  }
                >
                  Zgłoszenia
                </Tabs.Tab>
                <Tabs.Tab value="opony">Opony</Tabs.Tab>
                <Tabs.Tab value="przebieg">Przebieg</Tabs.Tab>
                <Tabs.Tab value="dostepnosc">Dostępność</Tabs.Tab>
                {v.can.costs && <Tabs.Tab value="koszty">Koszty i paliwo</Tabs.Tab>}
                <Tabs.Tab value="dokumenty">Dokumenty</Tabs.Tab>
                <Tabs.Tab value="historia">Historia zdarzeń</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panel value="podsumowanie">
                <SummaryTab me={me} v={v} />
              </Tabs.Panel>
              <Tabs.Panel value="przypisanie">
                <AssignmentTab v={v} />
              </Tabs.Panel>
              <Tabs.Panel value="terminy">
                <MaintenanceTab v={v} highlight={highlight} />
              </Tabs.Panel>
              <Tabs.Panel value="zgloszenia">
                <VehicleReportsTab v={v} />
              </Tabs.Panel>
              <Tabs.Panel value="opony">
                <TyresTab v={v} />
              </Tabs.Panel>
              <Tabs.Panel value="przebieg">
                <OdometerTab v={v} />
              </Tabs.Panel>
              <Tabs.Panel value="dostepnosc">
                <UnavailabilityTab v={v} />
              </Tabs.Panel>
              {v.can.costs && (
                <Tabs.Panel value="koszty">
                  <CostsTab v={v} />
                </Tabs.Panel>
              )}
              <Tabs.Panel value="dokumenty">
                <DocumentsTab v={v} />
              </Tabs.Panel>
              <Tabs.Panel value="historia">
                <EventsTab v={v} />
              </Tabs.Panel>
            </Tabs>
          </>
        )}
      </QueryState>
    </FleetShell>
  );
}
